import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export type ModalAction = "switch" | "back";

interface UseUnsavedChangesModalProps {
  activeTab: number;
  isDirty: { [key: number]: boolean };
  executeSaveDraft: (tabIndex: number) => void;
  executeResetForm: (tabIndex: number) => void;
}

/**
 * Hook to manage unsaved changes modal logic for tab switching and navigation
 */
export const useUnsavedChangesModal = ({
  activeTab,
  isDirty,
  executeSaveDraft,
  executeResetForm,
}: UseUnsavedChangesModalProps) => {
  const navigate = useNavigate();
  const [pendingTab, setPendingTab] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState<ModalAction>("switch");

  /**
   * Check if tab switch requires saving
   */
  const checkTabSwitch = useCallback(
    (newTab: number) => {
      if (isDirty[activeTab] && newTab !== activeTab) {
        setPendingTab(newTab);
        setModalAction("switch");
        setShowModal(true);
        return false; // Don't switch yet
      }
      return true; // Safe to switch
    },
    [activeTab, isDirty]
  );

  /**
   * Check if navigation back requires saving
   */
  const checkNavigateBack = useCallback(() => {
    const hasUnsavedChanges = Object.values(isDirty).some((dirty) => dirty);
    if (hasUnsavedChanges) {
      setModalAction("back");
      setShowModal(true);
      return false; // Don't navigate yet
    }
    navigate(-1);
    return true; // Navigated
  }, [isDirty, navigate]);

  /**
   * Handle save and proceed with action
   */
  const handleSaveAndProceed = useCallback(() => {
    executeSaveDraft(activeTab);
    setShowModal(false);

    if (modalAction === "switch" && pendingTab !== null) {
      return pendingTab; // Return new tab to switch to
    } else if (modalAction === "back") {
      navigate(-1);
    }
    setPendingTab(null);
    return null;
  }, [activeTab, executeSaveDraft, modalAction, navigate, pendingTab]);

  /**
   * Handle discard and proceed with action
   */
  const handleDiscardAndProceed = useCallback(() => {
    executeResetForm(activeTab);
    setShowModal(false);

    if (modalAction === "switch" && pendingTab !== null) {
      return pendingTab; // Return new tab to switch to
    } else if (modalAction === "back") {
      navigate(-1);
    }
    setPendingTab(null);
    return null;
  }, [activeTab, executeResetForm, modalAction, navigate, pendingTab]);

  /**
   * Cancel the modal action
   */
  const handleCancel = useCallback(() => {
    setShowModal(false);
    setPendingTab(null);
  }, []);

  return {
    showModal,
    modalAction,
    checkTabSwitch,
    checkNavigateBack,
    handleSaveAndProceed,
    handleDiscardAndProceed,
    handleCancel,
  };
};
